import React from 'react';
import {
  DataList,
  TextContent,
  Text,
  TextVariants,
  Flex,
  Spinner,
} from '@patternfly/react-core';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import type { IPTLLabComponent, LabDriverComponentProps } from '../types';
import './styles.scss';
import ProvisionDataItem from './ProvisionDataItem';

const PTLLabComponent: React.FC<LabDriverComponentProps> = ({
  labComponent,
}) => {
  const { t } = useTranslation();
  const ptlComponent = labComponent as IPTLLabComponent;

  const provisionData = ptlComponent.context?.stat?.provision_data as
    | Record<string, any>
    | undefined;

  const provisionDataAnnotations =
    ptlComponent.context?.provision_data_annotations || [];

  if (provisionDataAnnotations.length === 0 || !provisionData) {
    return (
      <section className="ptl-lab-component">
        <TextContent className="ptl-lab-component__title">
          <Text component={TextVariants.h2}>
            {ptlComponent.context?.title || t('Provision data')}
          </Text>
        </TextContent>
        <Flex
          justifyContent={{ default: 'justifyContentCenter' }}
          alignItems={{ default: 'alignItemsCenter' }}
          style={{ height: '30vh' }}
        >
          <Spinner />
        </Flex>
      </section>
    );
  }

  type ProvisionItem = {
    key: string;
    label: string;
    value: string;
    isSecret?: boolean;
    customValue?: React.ReactNode;
  };

  const items = provisionDataAnnotations.map((annotation) => {
    const val = String(_.get(provisionData, annotation.key, ''));

    const item: ProvisionItem = {
      key: annotation.key,
      label: annotation.label,
      value: val,
    };

    if (annotation.value_type === 'secret') {
      item.isSecret = true;
    }

    if (annotation.value_type === 'url') {
      item.customValue = (
        <Text component={TextVariants.p}>
          <a href={val} target="_blank" rel="noopener noreferrer">
            {val}
          </a>
        </Text>
      );
    }

    return item;
  });

  return (
    <section className="ptl-lab-component">
      <TextContent className="ptl-lab-component__title">
        <Text component={TextVariants.h2}>
          {ptlComponent.context?.title || t('Provision data')}
        </Text>
      </TextContent>

      <DataList isCompact aria-label={t('Lab provision data table')}>
        {items.map((item) => (
          <ProvisionDataItem key={item.key} item={item} />
        ))}
      </DataList>
    </section>
  );
};

export default PTLLabComponent;
